import React from 'react';
import { useHistory, useParams } from 'react-router';
import * as Yup from 'yup';
import { FormikField, ZenForm } from '../../../components/form';

import { useSitePublishDesignMutation } from '../../../generated/types';
import config from '../../../config/config';
import { LoadingButton } from '@mui/lab';
import { usernameRegex } from '../../../shared/utils/string';
import { Formik, FormikHelpers } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { ApiError } from '../../../shared/types/api-response';
import LinkIcon from '../../../components/icon/LinkIcon';
import { generatePathFromMenuName } from '../../../shared/utils/string/username';
import Dialog, { DialogProps } from '../../../components/dialog/Dialog';

export interface UsernameValues {
    username: string;
}

const usernameSchema = () =>
    Yup.object().shape({
        username: Yup.string()
            .required('URL is required.')
            .matches(usernameRegex, 'Please only use alphanumerical, minus, and underscore.')
            .max(30, 'Please use a maximum of 30 characters.'),
    });
export interface PublishModalProps extends DialogProps {
    username: string;
    onSuccess?: () => void;
}

const PublishModal: React.FC<PublishModalProps> = ({ username, onSuccess, ...props }) => {
    const { accountId } = useParams<{ accountId: string }>();
    const history = useHistory();

    const [publishSite] = useSitePublishDesignMutation();

    const onClickPublish = async (values: UsernameValues, formik: FormikHelpers<UsernameValues>) => {
        await publishSite({
            variables: {
                input: {
                    path: values.username,
                },
            },
            onError: (e: any) => {
                const err = e as ApiError;
                if (err.status === 400) {
                    formik.setFieldError('username', 'This URL is already taken');
                } else if (err.status === 500) {
                    enqueueSnackbar(`Can not ${accountId ? 'publish' : 'create'} design. Try again later!`, {
                        variant: 'error',
                    });
                } else {
                    formik.setFieldError('username', 'Can not publish design!');
                }
            },
            onCompleted() {
                onSuccess && onSuccess();
                history.push(`/account/${accountId}/publish-success`);
            },
        });
    };

    return (
        <Dialog anchor="bottom" showClose={false} {...props}>
            <div className="mt-20 px-28 pb-40 flex flex-col overflow-x-scroll">
                <div className="text-center">
                    <div className="font-bold text-25 leading-27 px-20">Choose a custom URL for your Menuzen.site.</div>
                    <div className="text-15 leading-16 text-primary-80 mt-10">
                        You’ll use this link to add your bio site to your existing social accounts, or quickly share
                        with customers.
                    </div>
                </div>
                <Formik
                    initialValues={{
                        username: username || '',
                    }}
                    validationSchema={usernameSchema}
                    enableReinitialize
                    onSubmit={(values, formikHelpers) =>
                        onClickPublish(values, formikHelpers as FormikHelpers<UsernameValues>)
                    }
                >
                    {({ handleSubmit, isSubmitting, values }) => (
                        <ZenForm type={`vertical`} className={`mt-20`} onSubmit={handleSubmit}>
                            <div className="p-10 bg-success-light rounded-6 mb-10">
                                <FormikField
                                    as={`text`}
                                    name={`username`}
                                    placeholder={`mybusiness`}
                                    size={`md`}
                                    className={`rounded-6 text-16`}
                                />
                                <div className="shadow-statusInput flex items-center justify-start text-12 leading-none mt-8 bg-white px-10 py-12 rounded-6">
                                    <LinkIcon />
                                    <p className="ml-3">{`${config.liveBioDomain}/${generatePathFromMenuName(
                                        values.username,
                                    )}`}</p>
                                </div>
                            </div>
                            <div className="mt-20">
                                <LoadingButton
                                    size={`large`}
                                    color={`primary`}
                                    variant={`contained`}
                                    fullWidth={true}
                                    type={`submit`}
                                    loading={isSubmitting}
                                >
                                    Publish
                                </LoadingButton>
                            </div>
                        </ZenForm>
                    )}
                </Formik>
            </div>
        </Dialog>
    );
};

export default PublishModal;
